import { usePortalMessage } from "../../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * The VoiceAgent's own SIP trunk numbers — the Channels tab (plan-008 ST-UA, design §22.14, R-UI-7).
 *
 * Each of the account's trunk numbers connected to this agent is a row that reads **Your SIP trunk**,
 * with the trunk's status as WeTalk observed it (verified, not verified yet, disabled), whether the
 * number receives conversations — or, in the words of design §22.5.2, the first reason it does not
 * yet — and whether the agent calls out from it (caller ID). A trunk whose provider sends calls to WeTalk's
 * registration (plan-008 ST-KM) also says what the registration front last reported ("Registered with your
 * provider", "Registration failed (SIP 403)", …). **Disconnect** unbinds the number.
 *
 * **Connect a trunk number** lists the account's trunk numbers that no agent answers yet and binds
 * the chosen one to this agent. With no trunk at all it reads **Connect a trunk** and opens the
 * trunk dialog Numbers & SIP uses (ST-UN's `SipTrunkDialog`, with WeTalk's SIP host from
 * `readSipTrunkSettings`, never a literal). Every change refreshes the trunk list the Numbers & SIP
 * card reads (the same query keys) and this agent's channel bindings.
 *
 * Disconnect, Connect and Connect a trunk are offered to Owners and Admins only (`sip_trunk.manage`, the
 * trunk routes' own role list, ST-90); a Member or a Viewer reads the rows and is told who can change them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Icon, Select, Tag, type TagStatus } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

// The trunk dialog is Numbers & SIP's, drawn by that screen's sheet; loaded here so it is styled
// when it opens from this tab without Numbers & SIP having been visited first.
import "../../../numbers/numbers.css";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import { QUERY_KEY } from "../../../../api/query.js";
import { isInboundOnlyTrunkView, type SipTrunkNumberView, type SipTrunkView } from "../../../../api/slice/sip-trunk.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { INBOUND_NOT_READY_WORD, TRUNK_STATE_WORD, registrationStateWords } from "../../../numbers/sip-trunk-copy.js";
import { SipTrunkDialog, type TrunkSaved } from "../../../numbers/sip-trunk-dialog.js";
import { SIP_TRUNK_QUERY_KEY, sipTrunkListQuery, sipTrunkSettingsQuery } from "../../../numbers/sip-trunk-query.js";
import { PartyAccessRequiredNote, isPartyAccessRequired } from "./access-binding-refusal.js";

/** One number of a trunk, with the trunk it is on. */
export interface TrunkNumberRow {
  readonly trunk: SipTrunkView;
  readonly number: SipTrunkNumberView;
}

/** The trunk numbers connected to this agent, and the account's numbers no agent answers yet. */
export function trunkNumbersOf(
  trunk: readonly SipTrunkView[],
  voiceAgentId: string,
): { readonly bound: TrunkNumberRow[]; readonly unbound: TrunkNumberRow[] } {
  const all = trunk.flatMap((one) => one.number.map((number) => ({ trunk: one, number })));
  return {
    bound: all.filter((row) => row.number.voiceAgentId === voiceAgentId),
    unbound: all.filter((row) => row.number.voiceAgentId === null),
  };
}

function stateTone(trunk: SipTrunkView): TagStatus {
  switch (trunk.state) {
    case "verified":
      return "positive";
    case "unverified":
      return "caution";
    case "disabled":
      return "muted";
  }
}

/**
 * The trunk's state in words. plan-015 SIP (ADR 0046): an inbound-only trunk is verified by its first answered
 * call, so until then it is "Waiting for the first call" — it already receives calls — rather than "Not verified
 * yet", which asks for a check it cannot have.
 */
export function trunkStateWordOf(trunk: SipTrunkView): string {
  return isInboundOnlyTrunkView(trunk) && trunk.state === "unverified" ? portalText("operations", "waiting_for_the_first_call_053d7b03") : TRUNK_STATE_WORD[trunk.state];
}

/** Whether the number receives conversations, or the first reason it does not (§22.5.2, B7). */
export function inboundWords(trunk: SipTrunkView): string {
  return trunk.inboundReadiness.ready
    ? portalText("operations", "receives_conversations_on_this_number_97e62e86")
    : portalText("operations", "does_not_receive_conversations_yet_value_e4bcde44", { v0: String(INBOUND_NOT_READY_WORD[trunk.inboundReadiness.reason]) });
}

/** A registration trunk's registration state in words; `null` for a direct trunk. */
export function registrationWordsOf(trunk: SipTrunkView): string | null {
  return trunk.inboundDelivery === "registration" ? registrationStateWords(trunk.registration) : null;
}

/** `+302101234567 · Our VoIP line · caller ID` — the row's one line of facts. */
export function trunkNumberLine(row: TrunkNumberRow): string {
  return `${row.number.e164} · ${row.trunk.displayName}${row.number.isCallerId ? portalText("operations", "caller_id_fca09f6b") : ""}`;
}

export function SipTrunkBindingPanel({ voiceAgentId }: { readonly voiceAgentId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const trunk = useQuery(sipTrunkListQuery(gateway));
  const settings = useQuery(sipTrunkSettingsQuery(gateway));
  const [chosen, setChosen] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saved, setSaved] = usePortalMessage();
  // Offered, never allowed: the trunk routes refuse a Member or a Viewer on their own (SIP_TRUNK_WRITE).
  const member = useMemberCan();
  const canManage = member.can("sip_trunk.manage");
  const refusal = member.whyNot("sip_trunk.manage");

  // Everything that says whether a number reaches this agent, not only the binding list: the
  // agent itself (its `reachable` drives "Live, not reachable" and "Next step: connect a number",
  // in the list and on this page), the connection panel, the concurrency summary and the
  // dashboard. Refreshing only the bindings left the page asking for a number it had (2026-10-02).
  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk }),
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.channelBinding, voiceAgentId] }),
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.connection, voiceAgentId] }),
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.concurrency, voiceAgentId] }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.number }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.dashboard }),
    ]);
  };

  const bind = useMutation({
    mutationFn: (row: TrunkNumberRow) =>
      gateway.bindSipTrunkNumber(row.trunk.sipTrunkId, row.number.phoneNumberId, voiceAgentId),
    onSuccess: async (number) => {
      setChosen("");
      setSaved(() => (portalText("operations", "value_now_reaches_this_agent_7143eb2d", { v0: String(number.e164) })));
      await refresh();
    },
  });

  const unbind = useMutation({
    mutationFn: (row: TrunkNumberRow) => gateway.unbindSipTrunkNumber(row.trunk.sipTrunkId, row.number.phoneNumberId),
    onSuccess: async (_, row) => {
      setSaved(() => (portalText("operations", "value_no_longer_reaches_this_agent_d205699b", { v0: String(row.number.e164) })));
      await refresh();
    },
  });

  if (trunk.isError) {
    return (
      <p className="c-note c-note-critical" role="alert">
        {portalText("operations", "your_sip_trunks_could_not_be_read_81563588")}{apiErrorText(trunk.error, portalText("operations", "try_again_in_a_minute_d4dad849"))}
      </p>
    );
  }
  if (trunk.data === undefined) return <p className="c-note">{portalText("operations", "reading_your_sip_trunk_1332067d")}</p>;

  const { bound, unbound } = trunkNumbersOf(trunk.data, voiceAgentId);
  const pick = unbound.find((row) => row.number.phoneNumberId === chosen) ?? unbound[0];

  return (
    <>
      {bound.length === 0 ? null : (
        <ul className="ch-list">
          {bound.map((row) => (
            <li className="ch-row" key={row.number.phoneNumberId}>
              <span className="ch-mark">
                <Icon glyph={GLYPH.phoneCall} size="xl" />
              </span>
              <span className="ch-row-body">
                <span className="ch-row-name">
                  <span>{portalText("operations", "your_sip_trunk_c26a097b")}</span>
                  <Tag status={stateTone(row.trunk)}>{trunkStateWordOf(row.trunk)}</Tag>
                </span>
                <span className="ch-row-dek">{inboundWords(row.trunk)}</span>
                {registrationWordsOf(row.trunk) === null ? null : (
                  <span className="ch-row-dek">{registrationWordsOf(row.trunk)}</span>
                )}
                <span className="ch-row-meta">{trunkNumberLine(row)}</span>
              </span>
              {canManage ? (
                <Button
                  variant="secondary"
                  disabled={unbind.isPending}
                  onClick={() => {
                    setSaved(null);
                    unbind.mutate(row);
                  }}
                >
                  {portalText("operations", "disconnect_ed28e068")}</Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <div className="ch-trunk-connect">
        <p className="ch-trunk-connect-title">{portalText("operations", "connect_a_trunk_number_2748c1eb")}</p>
        {!canManage ? (
          refusal === null ? null : <p className="ch-row-dek">{refusal}</p>
        ) : trunk.data.length === 0 ? (
          <>
            <p className="ch-row-dek">
              {portalText("operations", "bring_your_own_numbers_through_your_provider_s_sip_trunk_t_4e98c940")}</p>
            <div className="c-row">
              <Button
                variant="primary"
                onClick={() => {
                  setSaved(null);
                  setDialogOpen(true);
                }}
              >
                {portalText("operations", "connect_a_trunk_bed9b758")}</Button>
            </div>
          </>
        ) : unbound.length === 0 ? (
          <p className="ch-row-dek">
            {portalText("operations", "every_number_on_your_sip_trunk_already_reaches_an_agent_ad_eea044d0")}{" "}
            <Link to="/numbers">{portalText("operations", "numbers_sip_dad16cb4")}</Link>.
          </p>
        ) : (
          <div className="c-row">
            <Select
              aria-label={portalText("operations", "trunk_number_to_connect_bcbb1d3d")}
              value={pick?.number.phoneNumberId ?? ""}
              onChange={(event) => {
                setChosen(event.currentTarget.value);
              }}
            >
              {unbound.map((row) => (
                <option key={row.number.phoneNumberId} value={row.number.phoneNumberId}>
                  {trunkNumberLine(row)}
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              disabled={bind.isPending || pick === undefined}
              onClick={() => {
                if (pick === undefined) return;
                setSaved(null);
                bind.mutate(pick);
              }}
            >
              {portalText("operations", "connect_b65463cb")}</Button>
          </div>
        )}
        {saved === null ? null : (
          <p className="ch-note" role="status">
            {saved}
          </p>
        )}
        {bind.isError ? (
          isPartyAccessRequired(bind.error) ? (
            // plan-029 WAPP-ACCESS: an agent for the customer's own team needs its list first (access_list_required).
            <PartyAccessRequiredNote error={bind.error} voiceAgentId={voiceAgentId} />
          ) : (
            <p className="c-note c-note-critical" role="alert">
              {portalText("operations", "the_number_was_not_connected_0e627c68")}{apiErrorText(bind.error, portalText("operations", "nothing_changed_632fb89b"))}
            </p>
          )
        ) : null}
        {unbind.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {portalText("operations", "the_number_was_not_disconnected_9952feae")}{apiErrorText(unbind.error, portalText("operations", "nothing_changed_632fb89b"))}
          </p>
        ) : null}
      </div>

      {dialogOpen && canManage ? (
        <SipTrunkDialog
          trunk={null}
          settings={settings.data}
          onClose={() => {
            setDialogOpen(false);
          }}
          onSaved={(done: TrunkSaved) => {
            setDialogOpen(false);
            setSaved(
              () => (portalText("operations", "value_is_connected_and_not_verified_yet_check_its_connecti_a673f77b", { v0: String(done.displayName) })),
            );
            void refresh();
          }}
        />
      ) : null}
    </>
  );
}
